import React, { useState, useEffect } from "react";
import { SlArrowRight } from "react-icons/sl";
import { LoadingButton } from "../components/loadingButton";
import ConfirmationModal from "../components/ConfirmationModal.js";
import NotificationModal from "../components/NotificationModal.js";
import ApplicationDetailsModal from "../components/ApplicantDetailsModal.js";
import { IoSearch } from "react-icons/io5";
import apiClient from "../api/axiosInstance.js";
import PreviewModal from "../components/previewModal.js";
import { fetchApplications } from "../utilities/fetchAllApplications.js";

export const ApplicationsUnderReview = ({ activeNav }) => {
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortColumn, setSortColumn] = useState("");
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState(""); // "success" or "error"
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [filteredApplications, setFilteredApplications] = useState([]);

  const getApplications = async () => {
    try {
      const data = await fetchApplications();
      // Sort by createdAt date in descending order (latest applications first)
      const sortedApplications = data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setApplications(sortedApplications);
      // setApplications(data);
      setFilteredApplications(data);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };
  useEffect(() => {
    getApplications();
  }, []);

  //sorts fetched data when column is clicked
  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(order);

    const sortedApplications = [...filteredApplications].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredApplications(sortedApplications);
  };

  // filters the application based on application status an search term if entered
  useEffect(() => {
    const results = applications.filter(
      (applicant) =>
        `${applicant.surname} ${applicant.firstName} ${applicant.otherNames}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) &&
        applicant.applicationStatus === "Under Review"
    );
    setFilteredApplications(results);
  }, [searchTerm, applications]);

  // Approving Application by setting status to Approved
  const approveApplication = async (idPassportNumber) => {
    try {
      setLoading(true);
      const response = await apiClient.put(
        `/api/update-application/${idPassportNumber}`,
        {
          applicationStatus: "Approved",
        }
      );

      setModalMessage(
        "Application has been Approved successfully and an Acceptance letter sent to the Applicant's provided email address!"
      ); // Success message
      setModalType("success");
    } catch (error) {
      if (error.response && error.response.data.message) {
        // If the backend sent a message
        setModalMessage(error.response.data.message);
      } else {
        setModalMessage(
          "An error occurred. Please check your network connection and try again."
        );
      }
      setModalType("error");
    } finally {
      setLoading(false);
      setShowModal(true);
      setIsConfirmModalOpen(false);
      getApplications();
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Intl.DateTimeFormat("en-US", options).format(
      new Date(dateString)
    );
  };

  const handleViewClick = (applicant) => {
    setSelectedApplicant(applicant);
    setIsModalOpen(true); // Open the modal
  };

  // Handle opening the modal with the selected applicant's data
  const handleApproveClick = (applicant) => {
    setSelectedApplicant(applicant);
    setIsPreviewModalOpen(true);
  };

  const handleConfirmApproval = () => {
    setIsConfirmModalOpen(true);
    setIsPreviewModalOpen(false);
  };

  return (
    <div className="w-full bg-white border-b-2 shadow-lg px-2 lg:pr-5 pb-4 rounded-md overflow-x-auto h-[calc(100vh-14.2rem)]">
      <div className=" ">
        <div className=" flex flex-col sm:flex-row justify-between lg:items-center mb-2">
          <div className="flex lg:space-x-4">
            <SlArrowRight className="mt-2 lg:ml-4" />
            <h2 className="text-lg lg:text-xl ml-4 font-semibold">
              Applications Under Review
            </h2>
          </div>
          <div className="relative w-full sm:w-1/3 mt-4 mb-4 sm:mt-0">
            <input
              type="text"
              className="border border-green-700 rounded-xl p-2 pl-14 w-full"
              placeholder="Search for Applicant to Approve"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <IoSearch className="absolute left-5 top-3 text-green-700 text-xl" />
          </div>
        </div>
      </div>

      <table className="w-full table-auto  border-2 border-green-700 text-left border-collapse rounded-t-3xl lg:overflow-hidden">
        <thead className="border-b border-green-700">
          <tr className="bg-green-700 bg-opacity-85 text-white">
            <th className="p-2 lg:p-5 text-sm sm:text-base font-semibold min-w-[50px]">
              #
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[200px]">
              Applicant Name
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[80px] max-w-[150px]">
              Department
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[150px]">
              Phone Number
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[80px] max-w-[120px]">
              Nationality
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[150px]">
              Internship Start Date
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[150px]">
              Internship End Date
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[80px] max-w-[90px] ">
              Application Status
            </th>
            <th
              className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[150px] cursor-pointer"
              onClick={() => handleSort("createdAt")}
            >
              Application Date{" "}
              {sortColumn === "createdAt" && (
                <span>{sortOrder === "asc" ? "🔼" : "🔽"}</span>
              )}
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[50px] ">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredApplications.map((applicant, index) => (
            <tr
              key={index}
              className={`hover:bg-gray-300 transition duration-200 cursor-pointer ${
                index % 2 === 0
                  ? "bg-yellow-50 bg-opacity-50"
                  : "bg-gray-200 bg-opacity-50"
              }`}
              onClick={() => handleViewClick(applicant)}
            >
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 font-medium text-sm lg:text-[15px] text-center">
                {index + 1}.
              </td>
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 font-medium text-sm lg:text-[15px] truncate">{`${applicant.surname} ${applicant.firstName} ${applicant.otherNames}`}</td>
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 font-medium text-sm lg:text-[15px] truncate">
                {applicant.internshipDepartment}
              </td>
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 text-sm lg:text-base">
                {applicant.phoneNumber}
              </td>
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 text-sm lg:text-[15px] truncate">
                {applicant.nationality}
              </td>
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 text-sm lg:text-[15px]">
                {formatDate(applicant.internshipStartDate)}
              </td>
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 text-sm lg:text-[15px]">
                {formatDate(applicant.internshipEndDate)}
              </td>
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 text-sm lg:text-[15px] font-semibold">
                {applicant.applicationStatus}
              </td>
              <td className="px-2 py-2 lg:py-4 border-b border-gray-300 text-sm lg:text-[15px]">
                {formatDate(applicant.createdAt)}
              </td>
              <td
                className="px-2 py-1 border-b border-gray-300 text-[15px]"
                onClick={(e) => e.stopPropagation()} // Prevent row click from being triggered
              >
                <LoadingButton
                  className="bg-green-700 text-white py-[6px] px-5 rounded-lg text-sm"
                  loadingText="Approving..."
                  buttonText="Approve"
                  isLoading={loading[applicant.idPassportNumber]} // Track loading per application
                  onClick={() => {
                    setLoading((prev) => ({
                      ...prev,
                      [applicant.idPassportNumber]: true,
                    }));
                    handleApproveClick(applicant);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ApplicationDetailsModal
        isModalOpen={isModalOpen}
        selectedApplicant={selectedApplicant}
        setIsModalOpen={setIsModalOpen}
      />

      {/* Preview Modal */}
      <PreviewModal
        isOpen={isPreviewModalOpen}
        applicantDetails={selectedApplicant}
        onClose={() => {
          setLoading(false);
          setIsPreviewModalOpen(false);
        }}
        onConfirm={handleConfirmApproval}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setLoading(false);
          setIsConfirmModalOpen(false);
        }}
        onConfirm={() => approveApplication(selectedApplicant.idPassportNumber)}
        title="Confirm Application Approval"
        message={`Approve ${selectedApplicant?.surname} ${selectedApplicant?.firstName} ${selectedApplicant?.otherNames}'s internship application and send email with the Acceptance letter`}
      />

      <NotificationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        message={modalMessage}
        modalType={modalType}
      />
    </div>
  );
};
